import React, { useState, useEffect } from 'react'
import { Building2, ExternalLink, Loader, Shield } from 'lucide-react'

interface Tenant {
  id: string
  name: string
  domain: string
  createdAt: string
  settings?: {
    primaryColor?: string
    logo?: string
  }
}

interface TenantWithUrl extends Tenant {
  liveUrl?: string
}

const TrustCentersDirectory = () => {
  const [tenants, setTenants] = useState<TenantWithUrl[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadTenants()
  }, [])

  const loadTenants = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch('/api/public/trust-centers')
      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Failed to load trust centers: ${response.status} - ${errorText}`)
      }
      if (response.ok) {
        const data = await response.json()
        setTenants(data)
      }
    } catch (error) {
      console.error('Error loading trust centers:', error)
      setError(error instanceof Error ? error.message : 'Failed to load trust centers. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900">
      {/* Header */}
      <nav className="bg-gray-900/50 backdrop-blur-sm border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <a href="/" className="flex items-center">
            <Shield className="h-8 w-8 text-blue-400" />
            <span className="ml-3 text-2xl font-bold text-white">TrustCenterPage.com</span>
          </a>
          <div className="flex items-center gap-6">
            <a href="/directory" className="text-gray-300 hover:text-white">Directory</a>
            <a href="/pricing" className="text-gray-300 hover:text-white">Pricing</a>
            <a href="/admin" className="text-gray-300 hover:text-white">Company Login</a>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Trust Centers Directory
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Browse all available trust centers. Each company's security and compliance information in one place.
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <Loader className="w-12 h-12 animate-spin text-blue-400 mx-auto mb-4" />
            <p className="text-gray-300">Loading trust centers...</p>
          </div>
        ) : error ? (
          <div className="text-center py-12 bg-red-900/20 backdrop-blur-sm border border-red-700 rounded-xl">
            <Shield className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">Failed to load trust centers</h3>
            <p className="text-gray-300 mb-4">{error}</p>
            <button
              onClick={loadTenants}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              Retry
            </button>
          </div>
        ) : tenants.length === 0 ? (
          <div className="text-center py-12 bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl">
            <Shield className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No trust centers yet</h3>
            <p className="text-gray-400">Check back soon for available trust centers.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tenants.map((tenant) => (
              <a
                key={tenant.id}
                href={tenant.liveUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-6 hover:bg-gray-800/70 hover:border-gray-600 transition group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {tenant.settings?.logo ? (
                      <img
                        src={tenant.settings.logo}
                        alt={tenant.name}
                        className="w-12 h-12 object-contain"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-gray-700 rounded-lg flex items-center justify-center">
                        <Building2 className="w-7 h-7 text-gray-400" />
                      </div>
                    )}
                    <div>
                      <h3 className="font-bold text-white text-lg group-hover:text-blue-400 transition">
                        {tenant.name}
                      </h3>
                      <p className="text-sm text-gray-400">{tenant.domain}</p>
                    </div>
                  </div>
                  <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-blue-400 transition" />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <Shield className="w-4 h-4" />
                    <span>Security & Compliance</span>
                  </div>
                  {tenant.liveUrl && (
                    <p className="text-xs text-gray-500 truncate">
                      {tenant.liveUrl}
                    </p>
                  )}
                </div>
              </a>
            ))}
          </div>
        )}

        {/* Back to Home */}
        <div className="mt-16 text-center">
          <a
            href="/"
            className="text-gray-300 hover:text-white underline"
          >
            ← Back to Home
          </a>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900/50 backdrop-blur-sm border-t border-gray-800 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center text-gray-400">
          <p>&copy; {new Date().getFullYear()} Trust Center Platform. Built with Netlify.</p>
        </div>
      </footer>
    </div>
  )
}

export default TrustCentersDirectory
